import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Sparkles, Radio, CheckCircle2, AlertCircle, Zap, PlayCircle, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { ThreadLayer, CONN_COLORS } from "@/components/pms/ThreadLayer";
import CursorGuide from "@/components/pms/CursorGuide";
import { OTA_CATALOG, POPULAR_OTAS } from "@/lib/otaCatalog";
import { walkthroughFor } from "@/lib/otaWalkthroughs";

const DEMO_IDS = { "Booking.com": "BDC-88213, BDC-88214, BDC-88215", Airbnb: "ABNB-55201, ABNB-55202, ABNB-55203" };
const WALKTHROUGH_DONE_KEY = (pid, prov) => `atiti_walkthrough_done_${pid}_${prov}`;

export default function ChannelWizard() {
  const { pid } = useParams();
  const nav = useNavigate();
  const ref = useRef(null);
  const [d, setD] = useState(null);
  const [provider, setProvider] = useState("Booking.com");
  const [raw, setRaw] = useState("");
  const [fetched, setFetched] = useState({ found: [], failed: [], needs_credentials: [] });
  const [credsPrompt, setCredsPrompt] = useState(null); // { provider, external_ids, fields }
  const [credsInput, setCredsInput] = useState({});
  const [selUnit, setSelUnit] = useState(null);
  const [ai, setAi] = useState([]);
  const [busy, setBusy] = useState(false);
  const [guideN, setGuideN] = useState(0);
  const [otaQuery, setOtaQuery] = useState("");
  const [airbnbBusy, setAirbnbBusy] = useState(false);
  const [airbnbAutomapOpen, setAirbnbAutomapOpen] = useState(false);
  const [airbnbSynced, setAirbnbSynced] = useState([]); // listings pulled just now

  // Airbnb OAuth: after the host returns from Channex with ?airbnb_connected=1
  // & channel_id=…, sync the listing pool automatically then clean the URL.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("airbnb_connected") === "1" && q.get("channel_id")) {
      const cid = q.get("channel_id");
      setAirbnbBusy(true);
      api.post(`/pms/channels/airbnb/${cid}/sync-pool`)
        .then(({ data }) => {
          toast.success(`Airbnb connected · pulled ${data.synced} listing${data.synced === 1 ? "" : "s"}`);
          setProvider("Airbnb");
          setAirbnbSynced(data.listings || []);
          window.history.replaceState({}, "", window.location.pathname);
          load().then((fresh) => {
            // Auto-open the map-all-N dialog if this is a hotel-tier property
            // and the counts are useful (any listings pulled + any units set).
            const isHotel = (fresh?.property?.category || "homestay").toLowerCase() === "hotel";
            if (isHotel && (data.listings || []).length > 0 && (fresh?.units || []).length > 0) {
              setAirbnbAutomapOpen(true);
            }
          });
        })
        .catch((e) => toast.error(errMsg(e)))
        .finally(() => setAirbnbBusy(false));
    } else if (q.get("airbnb_failed") === "1") {
      toast.error("Airbnb OAuth was cancelled or failed — try again");
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAirbnbAutomap = async () => {
    setAirbnbBusy(true);
    try {
      const lids = airbnbSynced.map((l) => `ABNB-${l.id}`);
      const { data: r } = await api.post(`/pms/properties/${pid}/mappings/bulk-auto-multi`,
        { listing_ids: lids });
      let msg = `Auto-mapped ${r.mapped} Airbnb listing${r.mapped === 1 ? "" : "s"} to your units`;
      if (r.unused_listings?.length) msg += ` · ${r.unused_listings.length} listing(s) leftover`;
      if (r.unused_units?.length) msg += ` · ${r.unused_units.length} unit(s) still empty`;
      toast.success(msg);
      setAirbnbAutomapOpen(false);
      setAirbnbSynced([]);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setAirbnbBusy(false);
  };

  const startAirbnbOAuth = async () => {
    setAirbnbBusy(true);
    try {
      const { data: r } = await api.post(`/pms/properties/${pid}/channels/airbnb/connect`);
      if (r.authorize_url) {
        toast.success("Opening Airbnb consent screen in a new tab…");
        window.open(r.authorize_url, "_blank", "noopener,noreferrer");
      }
    } catch (e) { toast.error(errMsg(e)); }
    setAirbnbBusy(false);
  };

  const load = useCallback(() => api.get(`/pms/properties/${pid}`).then(({ data }) => { setD(data); return data; }).catch((e) => toast.error(errMsg(e))), [pid]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-slate-300" size={32} /></div>;
  const { property, units, channels, mappings, pool } = d;
  const unitName = (id) => units.find((u) => u.id === id)?.name || "?";
  const listingTitle = (lid) => pool.find((p) => p.listing_id === lid)?.title || lid;
  const connectedProviders = [...new Set(channels.map((c) => c.provider))];
  const liveProviders = [...new Set(channels.filter((c) => c.status === "live").map((c) => c.provider))];
  const otaMatches = otaQuery.trim()
    ? OTA_CATALOG.filter((o) => o.toLowerCase().includes(otaQuery.trim().toLowerCase()))
    : POPULAR_OTAS;
  const pickProvider = (p) => { setProvider(p); setFetched({ found: [], failed: [] }); setAi([]); setRaw(""); };

  const batchFetch = async (credentials) => {
    if (!raw.trim()) return toast.error("Paste your hotel/listing IDs first");
    setBusy(true);
    try {
      const payload = { provider, raw };
      // Guard: onClick passes a React SyntheticEvent — ignore it. We only
      // want the credentials object from `submitCreds`. Without this check
      // we'd try to JSON-stringify the event and hit the "circular
      // structure" axios error that triggered this fix.
      if (credentials && typeof credentials === "object" && !credentials.nativeEvent && !credentials.target) {
        payload.credentials = credentials;
      }
      const { data: r } = await api.post(`/pms/properties/${pid}/channels/batch-fetch`, payload);
      setFetched(r);
      setAi([]);
      // B2 — if any pasted ID is for a credentialed OTA (Expedia/Agoda/…),
      // Channex needs a username+password. Pop an inline dialog, collect
      // once, then repost the batch with those creds; the backend uses
      // the same credentials for every pasted ID of that provider.
      const nc = (r.needs_credentials || [])[0];
      if (nc) {
        setCredsPrompt({ provider: nc.provider, external_ids: (r.needs_credentials || []).map((x) => x.external_id),
                          fields: nc.fields, fieldMeta: nc.field_meta || [], message: nc.message });
        setCredsInput({});
      } else {
        setCredsPrompt(null);
      }
      const created = (r.found || []).filter((f) => f.created).length;
      const msg = `${r.found.length} ID(s) resolved`
        + (created ? ` (${created} auto-created on Channex)` : "")
        + (r.failed.length ? `, ${r.failed.length} failed — flagged inline` : "")
        + (nc ? ` — ${provider} needs credentials` : "");
      toast[r.found.length ? "success" : (nc ? "info" : "warning")](msg);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const submitCreds = async () => {
    const missing = (credsPrompt?.fields || []).filter((f) => !credsInput[f]?.trim());
    if (missing.length) return toast.error(`Please fill: ${missing.join(", ")}`);
    await batchFetch(credsInput);
  };

  const connect = async (unitId, ext, isAi) => {
    try {
      await api.post(`/pms/properties/${pid}/channels`, { unit_id: unitId, external_id: ext.external_id, provider: ext.provider, name: ext.name, ai: isAi,
        channex_channel_id: ext.channex_channel_id || null, hotel_id: ext.hotel_id || null, room_type_code: ext.room_type_code || null,
        rate_plan_code: ext.rate_plan_code || null, occupancy: ext.occupancy || null, pricing_type: ext.pricing_type || null });
      toast.success(`${ext.external_id} connected to Unit ${unitName(unitId)}`);
      setAi((s) => s.filter((x) => x.external_id !== ext.external_id));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const clickExternal = (ext) => {
    if (channels.some((c) => c.external_id === ext.external_id)) return;
    if (!selUnit) return toast.info("Tap a unit on the left first");
    connect(selUnit, ext, false);
    setSelUnit(null);
  };

  const suggest = async () => {
    if (fetched.found.length === 0) return toast.error("Fetch channel IDs first");
    setBusy(true);
    try {
      const { data: r } = await api.post("/pms/ai/suggest", { property_id: pid, phase: "channel", externals: fetched.found });
      const fresh = r.suggestions.filter((s) => !channels.some((c) => c.external_id === s.external_id));
      setAi(fresh);
      toast[fresh.length ? "success" : "info"](fresh.length ? `AI proposed ${fresh.length} channel match(es)` : "No AI matches found");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const goLive = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/pms/properties/${pid}/channels/golive`);
      toast.success(`${r.live} channel connection(s) are now LIVE across your OTAs`);
      const cx = r.channex || {};
      Object.values(cx).forEach((v) => { if (v && v.mapping && !v.mapping.ok) toast.error(`Channex mapping failed: ${JSON.stringify(v.mapping.reason).slice(0, 200)}`); });
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const extById = (eid) => fetched.found.find((f) => f.external_id === eid);
  const threadLinks = [
    ...channels.filter((c) => extById(c.external_id)).map((c, i) => ({ key: c.id, from: `cu-${c.unit_id}`, to: `ext-${c.external_id}`, color: CONN_COLORS[i % CONN_COLORS.length], dashed: false, onRemove: c.status === "live" ? null : async () => { await api.delete(`/pms/channels/${c.id}`); load(); } })),
    ...ai.filter((s) => extById(s.external_id)).map((s, i) => ({ key: `ai-${i}`, from: `cu-${s.unit_id}`, to: `ext-${s.external_id}`, color: "#4F46E5", dashed: true, onAccept: () => connect(s.unit_id, extById(s.external_id), true), onDismiss: () => setAi((x) => x.filter((_, j) => j !== i)) })),
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="backdrop-blur-xl bg-white/90 border-b border-slate-200/80 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button data-testid="back-to-property-button" onClick={() => nav(`/pms/property/${pid}`)} className="text-slate-400 hover:text-slate-700"><ArrowLeft size={18} /></button>
            <div>
              <h1 className="font-display font-bold text-slate-900 leading-tight">Channel Manager — {property.name}</h1>
              <p className="text-[11px] text-slate-400">PMS Active ≠ live on channels. Nothing goes live without your final confirmation.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {connectedProviders.length > 0 && (
              <span data-testid="ota-connected-count-badge" className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-slate-900 text-white flex items-center gap-1.5">
                <Radio size={12} className="text-emerald-400" /> {connectedProviders.length} of 150+ OTAs connected
              </span>
            )}
            <span className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center gap-1.5"><CheckCircle2 size={12} /> PMS Active</span>
            {property.channels_live && <span data-testid="channels-live-badge" className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-violet-50 text-violet-600 border border-violet-200 flex items-center gap-1.5"><Radio size={12} /> Live on channels</span>}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div data-testid="connectivity-status-row" className="bg-slate-900 text-white rounded-2xl px-6 py-4 flex items-center justify-between reveal">
          <div className="flex items-center gap-3">
            <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" /></span>
            <div>
              <p className="font-display font-bold text-sm">OTA connectivity ready</p>
              <p className="text-[11px] text-slate-400">Provisioned once by Atiti — you never touch provider credentials. Pick an OTA below and follow the 4-step guide inside its extranet.</p>
            </div>
          </div>
          <span className="font-mono text-xs text-emerald-400">99.99% sync uptime</span>
        </div>

        <WalkthroughCard pid={pid} provider={provider} />

        <div className="bg-white rounded-2xl border border-slate-200 p-6 reveal">
          <div className="mb-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Find your OTA — 150+ channels available</label>
              <span className="text-[11px] text-slate-400">{otaQuery.trim() ? `${otaMatches.length} match(es) in the catalog` : "Popular channels · type to search all 150+"}</span>
            </div>
            <input data-testid="ota-search-input" value={otaQuery} onChange={(e) => setOtaQuery(e.target.value)}
              placeholder="Search e.g. Goibibo, Agoda, Vrbo, Traveloka…"
              className="mt-2 w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {otaMatches.slice(0, 12).map((p) => (
                <button key={p} data-testid={`provider-tab-${p.replace(/[^a-zA-Z0-9]/g, "-")}`} onClick={() => pickProvider(p)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${provider === p ? "bg-slate-900 text-white border-transparent" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}>
                  {p}{connectedProviders.includes(p) && <span className="ml-1.5 text-emerald-400">●</span>}
                </button>
              ))}
              {otaQuery.trim() && otaMatches.length === 0 && <span className="text-xs text-slate-400">No OTA found — check the spelling or contact us to add it.</span>}
            </div>
            {!DEMO_IDS[provider] && <p className="text-[11px] text-slate-400 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mt-3 inline-block">No sample "Use demo IDs" button for {provider} — paste your real hotel/listing codes above. We'll auto-create the channel on Channex when you click Fetch.</p>}
          </div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Paste all your {provider} hotel/listing IDs at once (comma, space or newline separated)</label>
          {provider === "Airbnb" ? (
            <div data-testid="airbnb-oauth-panel" className="mt-2 rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/40 p-5 flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="font-display font-bold text-slate-900 text-sm">Airbnb doesn't use paste — it uses OAuth</p>
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  Click below to sign in to Airbnb and approve <b>Channex</b> as your channel manager. Every listing on your account will land in your pool automatically. No IDs to copy, no paperwork.
                </p>
              </div>
              <button data-testid="airbnb-oauth-connect-button" onClick={startAirbnbOAuth} disabled={airbnbBusy}
                className="btn-brand rounded-full px-5 py-2.5 text-xs font-semibold flex items-center gap-2 shrink-0">
                {airbnbBusy ? <Loader2 className="animate-spin" size={13} /> : <Zap size={13} />} Connect Airbnb via OAuth
              </button>
            </div>
          ) : (
            <textarea data-testid="bulk-id-textarea" value={raw} onChange={(e) => setRaw(e.target.value)} rows={2}
              placeholder={`e.g. ${DEMO_IDS[provider]}`}
              className="mt-2 w-full border border-slate-300 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
          )}
          <div className="flex items-center justify-between mt-3">
            {DEMO_IDS[provider] && provider !== "Airbnb" ? (
              <button onClick={() => setRaw(DEMO_IDS[provider])} className="text-xs text-slate-400 hover:text-slate-600 underline" data-testid="use-demo-ids-button">Use demo IDs</button>
            ) : <span />}
            {provider !== "Airbnb" && (
              <button data-testid="batch-fetch-button" onClick={() => batchFetch()} disabled={busy}
                className="btn-brand rounded-full px-5 py-2.5 text-xs font-semibold flex items-center gap-2">{busy ? <Loader2 className="animate-spin" size={13} /> : <Zap size={13} />} Fetch all in one pull</button>
            )}
          </div>
          {fetched.failed.length > 0 && (
            <div className="mt-4 space-y-1.5">
              {fetched.failed.map((f) => (
                <div key={f.external_id} data-testid={`failed-id-${f.external_id}`} className="flex items-center gap-2 text-xs bg-red-50 border border-red-200 text-red-600 rounded-lg px-3 py-2">
                  <AlertCircle size={13} /> <span className="font-mono">{f.external_id}</span> — {f.reason}. Successful IDs are ready below; fix and re-fetch this one anytime.
                </div>
              ))}
            </div>
          )}
          {credsPrompt && (
            <div data-testid="channex-creds-prompt" className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-3">
              <div className="flex items-start gap-2">
                <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">{credsPrompt.provider} needs one more detail</p>
                  <p className="text-xs text-amber-800 mt-0.5">{credsPrompt.message}</p>
                  <p className="text-[11px] text-amber-700 mt-1 font-mono">Applies to: {credsPrompt.external_ids.join(", ")}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {credsPrompt.fields.map((f) => {
                  const meta = (credsPrompt.fieldMeta || []).find((m) => m.name === f) || {};
                  return (
                  <div key={f}>
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{meta.label || f}</label>
                    <input data-testid={`creds-input-${f}`} type={meta.secret || f === "password" ? "password" : "text"}
                      value={credsInput[f] || ""} onChange={(e) => setCredsInput((s) => ({ ...s, [f]: e.target.value }))}
                      className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder={meta.label || f} autoComplete="new-password" />
                  </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-end gap-2">
                <button data-testid="creds-cancel" onClick={() => { setCredsPrompt(null); setCredsInput({}); }}
                  className="text-xs font-semibold px-4 py-2 rounded-full border border-slate-300 text-slate-500 hover:border-slate-500">Skip for now</button>
                <button data-testid="creds-submit" onClick={submitCreds} disabled={busy}
                  className="btn-brand rounded-full px-5 py-2 text-xs font-semibold flex items-center gap-2">
                  {busy ? <Loader2 className="animate-spin" size={13} /> : <Zap size={13} />} Connect {credsPrompt.provider}
                </button>
              </div>
            </div>
          )}
        </div>

        {fetched.found.length > 0 && (
          <div className="reveal">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-slate-600">Same gesture you learned in setup: tap a unit, then a channel listing. Dashed = AI proposal.</p>
              <div className="flex items-center gap-2">
                <button data-testid="guide-replay-channel" onClick={() => setGuideN((n) => n + 1)}
                  className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full border border-slate-300 text-slate-500 hover:border-slate-500 hover:text-slate-700 transition-colors">
                  <PlayCircle size={13} /> Show me how
                </button>
                <button data-testid="ai-suggest-channel-button" onClick={suggest} disabled={busy}
                  className="flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-full border border-[#4F46E5] brand-pink hover:bg-[#EEF2FF]">
                  {busy ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />} AI suggest matches
                </button>
              </div>
            </div>
            <div ref={ref} className="relative grid grid-cols-[1fr_120px_1.4fr] gap-0">
              <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2.5 self-start">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Units</p>
                {units.map((u) => (
                  <div key={u.id} data-conn-id={`cu-${u.id}`} data-testid={`ch-unit-${u.name}`} onClick={() => setSelUnit(selUnit === u.id ? null : u.id)}
                    className={`conn-row flex items-center justify-between border border-slate-200 rounded-xl px-4 py-3 ${selUnit === u.id ? "selected" : ""}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="h-8 px-2.5 min-w-[32px] max-w-[160px] rounded-lg bg-slate-900 text-white font-display font-bold text-xs flex items-center justify-center truncate">{u.name}</span>
                      <span className="text-xs text-slate-500 shrink-0">{mappings.filter((m) => m.unit_id === u.id).length} Atiti listing(s)</span>
                    </div>
                    <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                  </div>
                ))}
              </div>
              <div />
              <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2.5 self-start">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{provider} listings — fetched in one pull</p>
                {fetched.found.map((f) => {
                  const conn = channels.find((c) => c.external_id === f.external_id);
                  return (
                    <div key={f.external_id} data-conn-id={`ext-${f.external_id}`} data-testid={`ext-row-${f.external_id}`} onClick={() => clickExternal(f)}
                      className={`conn-row flex items-center gap-3 border border-slate-200 rounded-xl px-4 py-2.5 ${conn ? "bg-slate-50 cursor-default" : ""}`}>
                      <span className="w-2.5 h-2.5 rounded-full bg-slate-300 shrink-0" />
                      <span className="font-mono text-xs text-slate-400 w-24 shrink-0 truncate" title={f.external_id}>{f.hotel_id || f.external_id}</span>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm truncate ${conn ? "text-slate-400" : "text-slate-700"}`}>{f.name}</p>
                        <p className="text-[11px] text-slate-400">{f.city}</p>
                      </div>
                      {f.created && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600" title="Auto-created on Channex via POST /channels">just created</span>}
                      {conn && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${conn.status === "live" ? "bg-violet-50 text-violet-600" : "bg-emerald-50 text-emerald-600"}`}>{conn.status}</span>}
                    </div>
                  );
                })}
              </div>
              <ThreadLayer containerRef={ref} links={threadLinks} />
              {units.length > 0 && fetched.found.length > 0 && (
                <CursorGuide containerRef={ref} fromId={`cu-${units[0].id}`} toId={`ext-${fetched.found[0].external_id}`}
                  storageKey="atiti_guide_channel" playSignal={guideN}
                  messages={["Tap a unit…", "…then tap the channel listing that's the same physical space", "Connected! Press 'Confirm & go live' when everything's linked."]} />
              )}
            </div>
          </div>
        )}

        <section className="reveal">
          <h3 className="font-display font-bold text-slate-900">Unit identity cards</h3>
          <p className="text-xs text-slate-500 mt-0.5 mb-4">One physical unit, many names — your name is the primary identity; every channel alias threads back to it.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {units.map((u) => {
              const atitiListings = mappings.filter((m) => m.unit_id === u.id);
              const unitChans = channels.filter((c) => c.unit_id === u.id);
              return (
                <div key={u.id} data-testid={`identity-card-${u.name}`} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg hover:shadow-slate-200/70 transition-shadow">
                  <div className="flex items-center justify-between">
                    <span className="h-8 px-3 rounded-lg bg-slate-900 text-white font-display font-bold text-sm flex items-center justify-center max-w-[200px] truncate">{u.name}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Primary name</span>
                  </div>
                  <div className="mt-4 space-y-2">
                    {atitiListings.map((m) => (
                      <div key={m.id} className="flex items-start gap-2.5 text-sm">
                        <span className="shrink-0 mt-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#EEF2FF] brand-pink border border-[#C7D2FE]">Atiti</span>
                        <span className="text-slate-600 leading-snug">{listingTitle(m.listing_id)} <span className="font-mono text-[10px] text-slate-400">{m.listing_id}</span></span>
                      </div>
                    ))}
                    {unitChans.map((c) => (
                      <div key={c.id} className="flex items-start gap-2.5 text-sm">
                        <span className="shrink-0 mt-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">{c.provider}</span>
                        <span className="text-slate-600 leading-snug">{c.name || c.external_id} <span className="font-mono text-[10px] text-slate-400">{c.external_id}</span>
                          {c.status === "live" && <span className="ml-1.5 text-[10px] font-semibold text-violet-500">● live</span>}
                        </span>
                      </div>
                    ))}
                    {atitiListings.length === 0 && unitChans.length === 0 && (
                      <p className="text-xs text-slate-300 italic">No aliases yet — map listings or connect channels.</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 flex items-center justify-between reveal">
          <div>
            <h3 className="font-display font-bold text-slate-900">Connected channel listings: {channels.length}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              <span data-testid="ota-summary-count" className="font-semibold text-slate-700">{connectedProviders.length} of 150+ OTAs connected{liveProviders.length > 0 ? ` · ${liveProviders.length} live (${liveProviders.join(", ")})` : ""}.</span>{" "}
              {channels.filter((c) => c.status === "live").length} live · {channels.filter((c) => c.status === "connected").length} awaiting your go-live confirmation. {channels.map((c) => `${c.external_id}→${unitName(c.unit_id)}`).join(", ") || "None yet."}</p>
          </div>
          <div className="flex items-center gap-2">
            {property.channels_live && (
              <button data-testid="channel-launch-pms-button" onClick={() => nav("/pms/launch")}
                className="rounded-full px-5 py-3 text-sm font-semibold flex items-center gap-2 border border-slate-300 text-slate-600 hover:border-slate-500 bg-white">
                <Zap size={15} /> Launch PMS
              </button>
            )}
            <button data-testid="golive-button" onClick={goLive} disabled={busy || channels.length === 0}
              className="btn-brand rounded-full px-6 py-3 text-sm font-semibold flex items-center gap-2">
              {busy ? <Loader2 className="animate-spin" size={15} /> : <Radio size={15} />} Confirm & go live
            </button>
          </div>
        </div>
      </main>
      {airbnbAutomapOpen && (
        <div data-testid="airbnb-automap-modal" className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !airbnbBusy && setAirbnbAutomapOpen(false)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><Zap size={19} /></span>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-slate-900">One-click auto-map</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">We pulled <b>{airbnbSynced.length}</b> Airbnb listing{airbnbSynced.length === 1 ? "" : "s"}. Match them 1:1 to your <b>{d?.units?.length || 0}</b> units in one shot — same idea as the Atiti listing auto-map.</p>
              </div>
            </div>
            {airbnbSynced.length !== (d?.units?.length || 0) && (
              <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700 flex items-start gap-2">
                <AlertCircle size={13} className="shrink-0 mt-0.5" />
                Counts don't match exactly ({airbnbSynced.length} listings vs {d?.units?.length || 0} units). We'll auto-map <b>{Math.min(airbnbSynced.length, d?.units?.length || 0)}</b> pairs and leave the rest for you to map manually.
              </div>
            )}
            <div className="mt-4 max-h-64 overflow-y-auto space-y-1.5">
              {airbnbSynced.slice(0, Math.min(airbnbSynced.length, d?.units?.length || 0)).map((l, i) => (
                <div key={l.id} className="flex items-center gap-2.5 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <span className="shrink-0 h-6 px-2 min-w-[24px] rounded bg-slate-900 text-white font-display font-bold text-[10px] flex items-center justify-center">{d?.units?.[i]?.name}</span>
                  <span className="text-slate-400 shrink-0">←</span>
                  <span className="min-w-0 truncate text-slate-700">{l.title || `Airbnb ${l.id}`}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button data-testid="airbnb-automap-cancel" onClick={() => setAirbnbAutomapOpen(false)} disabled={airbnbBusy}
                className="text-xs font-semibold px-4 py-2 rounded-full border border-slate-300 text-slate-500 hover:border-slate-500">
                Not now
              </button>
              <button data-testid="airbnb-automap-confirm" onClick={runAirbnbAutomap} disabled={airbnbBusy || (d?.units?.length || 0) === 0}
                className="btn-brand rounded-full px-5 py-2 text-xs font-semibold flex items-center gap-2">
                {airbnbBusy ? <Loader2 className="animate-spin" size={13} /> : <Zap size={13} />}
                Auto-map {Math.min(airbnbSynced.length, d?.units?.length || 0)} pair{Math.min(airbnbSynced.length, d?.units?.length || 0) === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible per-OTA guide. On first open the host is walked through the
 * exact steps to link Channex inside the OTA's own extranet
 * (Booking.com → Connectivity provider · Airbnb → OAuth · Agoda → YCS, etc.).
 * State is persisted per-property + per-provider in localStorage so hosts
 * only see the guide until they've done it once — after that the card
 * collapses into a subtle "connectivity established" pill.
 */
function WalkthroughCard({ pid, provider }) {
  const g = walkthroughFor(provider);
  const doneKey = WALKTHROUGH_DONE_KEY(pid, provider);
  const [done, setDone] = useState(() => localStorage.getItem(doneKey) === "1");
  const [open, setOpen] = useState(() => localStorage.getItem(doneKey) !== "1");

  useEffect(() => {
    const persisted = localStorage.getItem(doneKey) === "1";
    setDone(persisted);
    setOpen(!persisted);
  }, [doneKey]);

  const markDone = () => { localStorage.setItem(doneKey, "1"); setDone(true); setOpen(false); };
  const redo = () => { localStorage.removeItem(doneKey); setDone(false); setOpen(true); };

  return (
    <div data-testid={`walkthrough-${provider}`}
      className={`rounded-2xl border p-5 reveal transition-colors ${done ? "bg-emerald-50/40 border-emerald-200" : "bg-white border-slate-200"}`}>
      <button onClick={() => setOpen((v) => !v)}
        data-testid={`walkthrough-toggle-${provider}`}
        className="w-full flex items-start justify-between gap-4 text-left">
        <div className="flex items-start gap-3 min-w-0">
          <span className={`shrink-0 mt-0.5 h-9 w-9 rounded-xl flex items-center justify-center ${done ? "bg-emerald-100 text-emerald-600" : "bg-indigo-50 text-indigo-600"}`}>
            {done ? <CheckCircle2 size={17} /> : <Radio size={17} />}
          </span>
          <div className="min-w-0">
            <p className="font-display font-bold text-slate-900 text-sm truncate">
              {done ? `${provider} connectivity established` : `Connect Channex inside ${g.extranet_name}`}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
              {done ? "You've completed the extranet steps. Paste additional listing IDs below any time." : `4 quick steps · takes ~2 minutes. Do them in ${g.extranet_name}, then come back and paste your listing IDs.`}
            </p>
          </div>
        </div>
        <span className="shrink-0 flex items-center gap-2">
          {done && <button onClick={(e) => { e.stopPropagation(); redo(); }} data-testid={`walkthrough-redo-${provider}`}
            className="text-[10px] font-semibold text-slate-400 hover:text-slate-700 underline">Redo guide</button>}
          {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </span>
      </button>

      {open && (
        <div className="mt-5 space-y-3">
          {g.extranet_url && (
            <a href={g.extranet_url} target="_blank" rel="noopener noreferrer"
              data-testid={`walkthrough-open-extranet-${provider}`}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full border border-slate-300 text-slate-600 hover:border-slate-500 hover:text-slate-900 bg-white transition-colors">
              <ExternalLink size={11} /> Open {g.extranet_name} ↗
            </a>
          )}
          <ol className="space-y-3 mt-2">
            {g.steps.map((s, i) => (
              <li key={i} data-testid={`walkthrough-step-${provider}-${i + 1}`}
                className="flex gap-3">
                <span className="shrink-0 h-7 w-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <div className="min-w-0 pt-0.5">
                  <p className="text-sm font-semibold text-slate-900">{s.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{s.body}</p>
                  {s.link_url && (
                    <a href={s.link_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:underline mt-1">
                      {s.link_label || "Open ↗"} <ExternalLink size={10} />
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ol>
          {g.id_hint && (
            <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600">
              <b>Where to find the IDs:</b> {g.id_hint}
            </div>
          )}
          <div className="flex items-center gap-2 pt-2">
            <button data-testid={`walkthrough-done-${provider}`} onClick={markDone}
              className="btn-brand rounded-full px-5 py-2.5 text-xs font-semibold flex items-center gap-2">
              <CheckCircle2 size={13} /> I've done this in {g.extranet_name}
            </button>
            <span className="text-[11px] text-slate-400">You can now paste your listing IDs below.</span>
          </div>
        </div>
      )}
    </div>
  );
}
